/**
 * Book file management and cloud operations for ApiClient (mixin pattern).
 * These functions use `this` and are assigned to ApiClient.prototype.
 *
 * Format-specific behavior (metadata extraction, cover extraction, chunk
 * preprocessing) is dispatched through the format registry — never via
 * hardcoded `fileType === 'epub'` checks. Adding a new format means adding
 * an entry to lib/formats/registry.js, never editing this file.
 */

import * as localDB from '../localDB';
import { performSync } from '../syncService';
import { extractBookCover } from '../coverExtractor';
import { reportError } from '../error-reporter';
import { API_BASE, blobUrlCache, dataUrlToBlob, fetchWithRetry } from './helpers';
import { isFeatureEnabled } from '../featureFlags';
import { parseXML, parseOPF, parseTOC } from '../epub/parseEpub';
import { mergeChapters } from '../epub/chapterMerger';
import { getFormatConfig, getMimeType } from '../formats/registry';

/**
 * Extract a normalized ISBN (digits-only) from an array of dc:identifier strings.
 * Returns the first valid ISBN-13 or ISBN-10 found, or null.
 */
function pickIsbn(identifiers) {
    if (!identifiers?.length) return null;
    for (const raw of identifiers) {
        const cleaned = String(raw).replace(/^(urn:isbn:|isbn[-\s]?1[03]?:?\s*|isbn:?\s*)/i, '').replace(/\s/g, '');
        if (!/^[\d-]{10,17}$/.test(cleaned)) continue;
        const digits = cleaned.replace(/-/g, '');
        if (digits.length === 13 || digits.length === 10) return digits;
    }
    return null;
}

/**
 * Check if a book with the same title+author+size already exists.
 * Returns { book, isPhantom } if found, null otherwise.
 * A "phantom" is a book record with neither local file nor cloud file_url.
 */
export async function findDuplicateBook(file) {
    const fileType = this.getFileType(file.name);
    let metadata = { title: null, author: null };
    const config = getFormatConfig(fileType);
    if (config?.metadataExtractor) {
        const arrayBuffer = await file.arrayBuffer();
        const mod = await config.metadataExtractor();
        try {
            metadata = await mod.extract(arrayBuffer);
        } catch (e) {
            // Hard errors that the upload flow also can't handle → re-throw
            // so handleUpload sees them and shows a specific message.
            // DRM / HUFF can't be read at all, so even filename-based dedup
            // is pointless. (KF8 used to be in this list; the AZW3 parser
            // now handles it natively, so KF8 metadata extraction succeeds
            // through parseMobiMetadata's record-0 EXTH path.)
            if (
                e?.code === 'DRM_PROTECTED' ||
                e?.code === 'HUFF_NOT_SUPPORTED'
            ) {
                throw e;
            }
            // Soft errors (corrupt/unreadable but uploadable) → fall back to
            // filename-only dedup. uploadBook will encounter the same error
            // and decide whether to accept or reject the file.
            console.warn(`[findDuplicateBook] ${fileType} metadata failed, falling back to filename:`, e);
        }
    }
    const filenameMeta = this.constructor.parseFilenameMetadata(file.name);
    const title = metadata.title || filenameMeta.title;
    const author = metadata.author || filenameMeta.author || null;

    const { books } = await this.getBooks();
    const localFileIds = await localDB.getLocalFileBookIds();
    const match = books.find(b =>
        b.title === title &&
        b.author === author &&
        b.file_size === file.size &&
        !b.deleted_at
    );
    if (!match) return null;
    const isPhantom = !localFileIds.has(match.id) && !match.file_url;
    return { book: match, isPhantom };
}

/**
 * Upload a book file. If reuseBookId is provided (phantom revive),
 * reuses that ID and updates the existing record instead of creating a new one.
 */
export async function uploadBook(file, reuseBookId) {
    const bookId = reuseBookId || this.generateId();
    const arrayBuffer = await file.arrayBuffer();
    const fileType = this.getFileType(file.name);
    const config = getFormatConfig(fileType);

    let metadata = { title: null, author: null, language: null, identifiers: [] };
    if (config?.metadataExtractor) {
        const mod = await config.metadataExtractor();
        try {
            metadata = await mod.extract(arrayBuffer);
        } catch (e) {
            // Known format-level hard errors — re-throw with their original
            // code so Library.jsx can display a specific error message.
            // (KF8 dropped from this list once the AZW3 adapter landed —
            // KF8 files now parse end-to-end via kf8BookParser.)
            if (
                e?.code === 'DRM_PROTECTED' ||
                e?.code === 'HUFF_NOT_SUPPORTED'
            ) {
                throw e;
            }
            // Any other error means the file is corrupt / unreadable. Refuse
            // the upload rather than silently continuing with null metadata
            // (which would look to the user like the filename becoming the
            // title with no explanation). Wrap so the code field is stable.
            const wrapped = new Error(
                `Failed to read ${fileType.toUpperCase()} metadata: ${e?.message || 'unknown error'}`
            );
            wrapped.code = `${fileType.toUpperCase()}_PARSE_FAILED`;
            wrapped.cause = e;
            throw wrapped;
        }
    }

    const filenameMeta = this.constructor.parseFilenameMetadata(file.name);

    await localDB.saveBookFile(bookId, arrayBuffer);

    // PDF upload has a race window: createBook writes the IDB row first,
    // then uploadBookToCloud takes seconds-to-tens-of-seconds to land. If
    // the user clicks the card during that window, conversion_status is
    // undefined — Library.handleOpenBook fails to intercept (matches only
    // 'pending_preview') and Reader's dispatch falls through to a fake
    // "converting…" UI. Set 'uploading' upfront so both surfaces have a
    // truthy state to gate on; uploadBookToCloud flips it to
    // 'pending_preview' once the backend response lands.
    const initialConversionStatus = fileType === 'pdf' ? 'uploading' : undefined;

    let book;
    if (reuseBookId) {
        // Revive phantom: update existing record, preserve created_at / canonical_book_id / etc.
        // Cap file_name to backend medium=500 limit (z-library names can blow past it).
        book = await localDB.updateBook(bookId, {
            file_name: typeof file.name === 'string' && file.name.length > 500 ? file.name.slice(0, 500) : file.name,
            file_type: fileType,
            file_size: file.size,
            ...(initialConversionStatus ? { conversion_status: initialConversionStatus } : {}),
        });
        if (!book) {
            // Phantom was deleted between findDuplicateBook and uploadBook — fall back to create
            reuseBookId = null;
        }
    }
    if (!reuseBookId) {
        // Caps mirror backend validate.ts LIMITS (title: short=200, author:
        // medium=500). Real-world PDFs from z-library / Anna's Archive often
        // ship 200+ char filenames with full subtitles + author list; without
        // capping here the upload fails on POST /reader/books with a 400
        // "title too long" that surfaces as a confusing "Book not found"
        // downstream when the subsequent /file POST hits an absent row.
        const cap = (s, n) => (typeof s === 'string' && s.length > n ? s.slice(0, n) : s);
        book = await localDB.createBook(this.userId, {
            id: bookId,
            title: cap(metadata.title || filenameMeta.title, 200),
            author: cap(metadata.author || filenameMeta.author || null, 500),
            language: (() => {
                const raw = metadata.language?.split(/[-_]/)[0]?.toLowerCase();
                return (raw && raw !== 'und' && raw !== 'undetermined') ? raw : null;
            })(),
            isbn: pickIsbn(metadata.identifiers),
            file_name: cap(file.name, 500),
            file_type: fileType,
            file_size: file.size,
            ...(initialConversionStatus ? { conversion_status: initialConversionStatus } : {}),
        });
    }

    // PDF requires server-side conversion (pdf-conversion-worker). Unlike
    // EPUB/MOBI which live local-first and sync lazily, a PDF cannot be
    // opened until the server produces an EPUB derivative. Kick off the
    // cloud upload immediately — sync alone is insufficient because the
    // backend /reader/sync endpoint rejects new conversion-format rows
    // (see lens/backend/src/routes/reader/sync.ts A5 guard). Anonymous users
    // cannot use PDFs; surface a clear error instead of silently skipping.
    if (fileType === 'pdf') {
        if (!this.token) {
            const needLogin = new Error('PDF conversion requires a signed-in account');
            needLogin.code = 'PDF_REQUIRES_LOGIN';
            throw needLogin;
        }
        try {
            await this.uploadBookToCloud(bookId);
        } catch (e) {
            console.warn('[uploadBook] PDF cloud upload failed:', e);
            reportError(e?.message || 'PDF cloud upload failed', {
                level: 'error',
                stack: e?.stack,
                metadata: { source: 'uploadBook.pdf', bookId },
            });
            // Clean up the half-baked row so it doesn't sit on the shelf
            // forever stuck at 'uploading'. Soft-delete + file evict; if the
            // backend somehow did write pdf_conversion_state before the
            // response failed, the next sync poll will reconcile by
            // recreating the local row with the real 'pending_preview' state.
            try {
                await localDB.deleteBook(bookId, this.userId);
                await localDB.deleteBookFile(bookId);
            } catch (cleanupErr) {
                console.warn('[uploadBook] cleanup after upload failure also failed:', cleanupErr);
            }
            throw e;
        }
        // We deliberately KEEP the local PDF bytes in book_files for now.
        // Eviction happens later, in syncService.js, when the sync sees the
        // file_type flip to 'epub_from_pdf' (i.e., conversion completed).
        // Why not evict here? Library.handleOpenBook gates navigation on
        // (hasLocalFile || hasCloudFile). During the 30-90s conversion
        // window, file_url stays null — so without a local file, the user
        // would be blocked from even opening the book to see the
        // "converting" placeholder. Keeping the PDF bytes lets localFileIds
        // include this book and lets Library navigate to Reader; Reader.jsx
        // then dispatches to the conversion-pending placeholder. Once
        // conversion completes, sync's magic-byte check evicts the bytes
        // and a subsequent open downloads the converted EPUB.
        // uploadBookToCloud wrote source_pdf_url + conversion_status='pending'
        // into localDB. Reload the book so the returned object reflects that.
        book = await localDB.getBook(bookId) || book;
        return { book };
    }

    this.triggerSync();

    // Fire-and-forget: extract and cache cover to IndexedDB for instant display.
    // The format registry decides whether the format supports cover extraction —
    // we never hardcode 'epub' here.
    if (config?.coverExtractor) {
        extractBookCover(arrayBuffer, fileType).then(dataUrl => {
            if (dataUrl) localDB.saveBookCover(bookId, dataUrlToBlob(dataUrl));
        }).catch(() => {});
    }

    // Fire-and-forget: pre-process for instant chunk display (canonical match)
    if (config?.preProcessor && this.token) {
        try {
            const mod = await config.preProcessor();
            mod.process.call(this, bookId, arrayBuffer, book).catch(() => {});
        } catch (e) {
            console.warn(`[uploadBook] ${fileType} preprocessor load failed:`, e);
        }
    }

    return { book };
}

export async function deleteBook(id) {
    // Delete file and cached cover from IndexedDB
    await localDB.deleteBookFile(id);
    await localDB.deleteBookCover(id);

    // Revoke blob URL if cached
    if (blobUrlCache.has(id)) {
        URL.revokeObjectURL(blobUrlCache.get(id));
        blobUrlCache.delete(id);
    }

    // Soft-delete locally (sets deleted_at, adds pending change for sync)
    await localDB.deleteBook(id, this.userId);

    // Also delete the file from R2 (cloud) if it was uploaded
    if (this.token) {
        try {
            await fetch(`${API_BASE}/reader/books/${id}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${this.token}` },
            });
        } catch (e) {
            console.warn('[deleteBook] Failed to delete from server:', e.message);
            reportError(e.message || 'Failed to delete book from server', { level: 'warn', stack: e.stack, metadata: { source: 'api.deleteBook', bookId: id } });
        }
    }

    this.triggerSync();
    return { success: true };
}

/**
 * Free local storage by removing the local file only.
 * Book metadata and cloud file (file_url) remain intact.
 */
export async function freeLocalSpace(id) {
    await localDB.deleteBookFile(id);
    if (blobUrlCache.has(id)) {
        URL.revokeObjectURL(blobUrlCache.get(id));
        blobUrlCache.delete(id);
    }
}

/**
 * Upload book file and progress to Cloudflare R2.
 */
export async function uploadBookToCloud(bookId) {
    if (!this.token) {
        throw new Error('Not authenticated');
    }
    const book = await localDB.getBook(bookId);
    if (!book) {
        throw new Error('Book not found');
    }
    const arrayBuffer = await localDB.getBookFile(bookId);
    if (!arrayBuffer) {
        throw new Error('Book file not found');
    }

    // Step 1: Ensure the book record exists in D1
    try {
        const createResp = await fetchWithRetry(`${API_BASE}/reader/books`, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${this.token}`,
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                id: book.id,
                title: book.title,
                author: book.author || null,
                file_name: book.file_name,
                file_type: book.file_type,
                file_size: book.file_size ?? null,
                language: book.language ?? null,
            }),
        });
        if (!createResp.ok) {
            // A failed create is fatal for the upload: continuing would push a
            // file for a book row the server refused (e.g. 403 BOOK_LIMIT).
            // Same structured shape as the Step-3 error below.
            const errData = await createResp.json().catch(() => ({}));
            const e = new Error(errData.error || `Book create failed: ${createResp.status}`);
            e.status = createResp.status;
            e.code = errData.code || errData.error || null;
            e.detail = errData;
            throw e;
        }
    } catch (e) {
        console.warn('[uploadBookToCloud] Book create/upsert failed:', e.message);
        reportError(e.message || 'Book create/upsert failed during cloud upload', { level: 'warn', stack: e.stack, metadata: { source: 'api.uploadBookToCloud', bookId } });
        throw e;
    }

    // Step 2: Also try sync to push all pending changes (non-blocking)
    try {
        await performSync(this.token);
    } catch (e) {
        console.warn('[uploadBookToCloud] Sync failed (non-critical):', e.message);
        reportError(e.message || 'Sync failed during cloud upload', { level: 'warn', stack: e.stack, metadata: { source: 'api.uploadBookToCloud.sync', bookId } });
    }

    // Step 3: Upload the file to R2
    const progress = await localDB.getProgress(bookId, this.userId);
    const contentType = getMimeType(book.file_type);
    const response = await fetchWithRetry(`${API_BASE}/reader/books/${bookId}/file`, {
        method: 'POST',
        headers: {
            'Authorization': `Bearer ${this.token}`,
            'Content-Type': contentType,
            'X-Progress': btoa(unescape(encodeURIComponent(JSON.stringify(progress || {})))),
        },
        body: arrayBuffer,
    });
    if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        // Attach structured fields so the auto-backup scan can tell a hard
        // quota rejection (403 storage_quota_exceeded — stop retrying, prompt
        // upgrade) from a transient error (fetchWithRetry already retried 5xx).
        const e = new Error(data.error || `Upload failed: ${response.status}`);
        e.status = response.status;
        // Prefer the explicit `code` field (e.g. BOOK_LIMIT); fall back to the
        // `error` string for endpoints that don't send one (the storage 403's
        // 'storage_quota_exceeded', which runCloudBackupScan matches on).
        e.code = data.code || data.error || null;
        e.detail = data;
        throw e;
    }
    const data = await response.json();

    // Backend returns one of two shapes:
    //   - 201 { file_url } for EPUB/MOBI → regular local-first book
    //   - 202 { source_pdf_url, conversion_status: 'pending' } for PDF →
    //     conversion queued; file_url stays null until pdf-conversion-worker
    //     finishes, which syncService.js will pick up via the 30s poll.
    //
    // 2026-05-14: removed the early-return that skipped cover extraction
    // for PDFs (previously assumed the EPUB derivative would expose a
    // proper cover via OPF metadata, but the Mistral-OCR-produced EPUBs
    // don't mark a cover-image item, so extractEpubCover falls through to
    // "first image in manifest" which is whatever Mistral found on page 1
    // of body content — wrong picture). We now render page 1 of the source
    // PDF directly to a cover at upload time, which is the only reliable
    // way to get the actual book jacket.
    const isPdfPending = !!data.source_pdf_url && !data.file_url;
    if (isPdfPending) {
        await localDB.updateBook(bookId, {
            source_pdf_url: data.source_pdf_url,
            conversion_status: data.conversion_status || 'pending',
        });
    } else {
        await localDB.updateBook(bookId, { file_url: data.file_url });
    }

    // Step 4: Extract and upload cover image (non-blocking, best-effort).
    // Format registry decides whether the format supports cover extraction.
    // For PDFs we use the PDF coverExtractor (renders page 1 via pdfjs);
    // for EPUB/MOBI we use the format-native one. Both go through the same
    // extractBookCover dispatcher.
    if (getFormatConfig(book.file_type)?.coverExtractor && !book.cover_url) {
        try {
            const coverDataUrl = await extractBookCover(arrayBuffer, book.file_type);
            if (coverDataUrl) {
                const coverBlob = dataUrlToBlob(coverDataUrl);
                const coverResp = await fetch(`${API_BASE}/reader/books/${bookId}/cover`, {
                    method: 'POST',
                    headers: {
                        'Authorization': `Bearer ${this.token}`,
                        'Content-Type': coverBlob.type,
                    },
                    body: coverBlob,
                });
                if (coverResp.ok) {
                    const coverData = await coverResp.json();
                    await localDB.updateBook(bookId, { cover_url: coverData.cover_url });
                }
            }
        } catch (e) {
            console.warn('[uploadBookToCloud] Cover upload failed (non-critical):', e.message);
            reportError(e.message || 'Cover upload failed during cloud upload', { level: 'warn', stack: e.stack, metadata: { source: 'api.uploadBookToCloud.cover', bookId } });
        }
    }

    if (isPdfPending) {
        return { success: true, conversion_status: data.conversion_status || 'pending' };
    }

    this.triggerSync();
    return { success: true, file_url: data.file_url };
}

/**
 * Fetch a book cover image from cloud via the cover API.
 */
export async function fetchBookCoverFromCloud(bookId) {
    if (!this.token) return null;
    try {
        const response = await fetch(`${API_BASE}/reader/books/${bookId}/cover`, {
            headers: { 'Authorization': `Bearer ${this.token}` },
        });
        if (!response.ok) return null;
        const blob = await response.blob();
        // Cache in IndexedDB for instant load on next visit
        localDB.saveBookCover(bookId, blob).catch(() => {});
        return URL.createObjectURL(blob);
    } catch {
        return null;
    }
}

/**
 * Fetch book file from cloud, save to IndexedDB.
 */
export async function fetchBookFileFromCloud(id, onProgress) {
    if (!this.token) return null;
    const book = await localDB.getBook(id);
    if (!book) return null;
    const url = `${API_BASE}/reader/books/${id}/file`;
    try {
        const response = await fetch(url, {
            headers: { Authorization: `Bearer ${this.token}` },
        });
        if (!response.ok) return null;

        // Stream the response to track download progress
        const contentLength = parseInt(response.headers.get('content-length') || '0', 10);
        if (onProgress && contentLength > 0 && response.body) {
            const reader = response.body.getReader();
            const chunks = [];
            let received = 0;

            while (true) {
                const { done, value } = await reader.read();
                if (done) break;
                chunks.push(value);
                received += value.length;
                onProgress(Math.round((received / contentLength) * 100));
            }

            const arrayBuffer = new Uint8Array(received);
            let offset = 0;
            for (const chunk of chunks) {
                arrayBuffer.set(chunk, offset);
                offset += chunk.length;
            }
            const buffer = arrayBuffer.buffer;
            if (buffer.byteLength > 0) {
                // Pin the downloaded file_url onto the blob so future syncs can
                // detect "this blob is from an OLD file_url" even after the
                // books-store entry has been overwritten with a new file_url.
                // §F17: also pin epub_version — same mechanism, but keyed on
                // bumped counter to catch same-path overwrites (incremental
                // PDF→EPUB partial publishes).
                await localDB.saveBookFile(id, buffer, {
                    file_url: book?.file_url ?? null,
                    epub_version: book?.epub_version ?? 0,
                });
                // Extract and cache cover for instant library display
                if (getFormatConfig(book?.file_type)?.coverExtractor) {
                    extractBookCover(buffer, book.file_type).then(dataUrl => {
                        if (dataUrl) localDB.saveBookCover(id, dataUrlToBlob(dataUrl));
                    }).catch(() => {});
                }
                return buffer;
            }
            return null;
        }

        // Fallback: no content-length or no body stream
        const arrayBuffer = await response.arrayBuffer();
        if (arrayBuffer.byteLength > 0) {
            await localDB.saveBookFile(id, arrayBuffer, {
                file_url: book?.file_url ?? null,
                epub_version: book?.epub_version ?? 0,   // §F17
            });
            if (onProgress) onProgress(100);
            // Extract and cache cover for instant library display
            if (getFormatConfig(book?.file_type)?.coverExtractor) {
                extractBookCover(arrayBuffer, book.file_type).then(dataUrl => {
                    if (dataUrl) localDB.saveBookCover(id, dataUrlToBlob(dataUrl));
                }).catch(() => {});
            }
            return arrayBuffer;
        }
        return null;
    } catch {
        return null;
    }
}

/**
 * Re-run the PDF→EPUB conversion pipeline against the preserved source PDF.
 * Used by the Toolbar's "升级 / Upgrade" button to convert legacy books
 * (pre-v1.2 prompt version, missing `data-pdf-page` attrs needed for 对照
 * mode sync) without forcing the user to delete + re-upload.
 *
 * Backend flips conversion_status back to 'pending' and clears file_url.
 * Reader.jsx's dispatch routes to the converting placeholder; the existing
 * progress poll auto-transitions to DualViewReader once conversion lands.
 *
 * Locally we mirror those field changes so the UI re-dispatches immediately
 * without waiting for the next sync poll.
 */
export async function reconvertBook(id) {
    if (!this.token) throw new Error('Reconvert requires authentication');
    const resp = await fetch(`${API_BASE}/reader/books/${id}/reconvert`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${this.token}` },
    });
    if (!resp.ok) {
        const data = await resp.json().catch(() => ({}));
        throw new Error(data.error || `Reconvert failed: ${resp.status}`);
    }
    // Mirror server state into local IDB so Reader.jsx dispatches to the
    // converting placeholder on the next render without waiting for sync.
    try {
        await localDB.updateBook(id, {
            conversion_status: 'pending',
            conversion_error: null,
            file_url: null,
        });
    } catch (e) {
        console.warn('[reconvertBook] local mirror update failed:', e);
    }
    return await resp.json();
}

/**
 * Fetch the original source PDF for a converted book (file_type 'epub_from_pdf').
 * Hits `/reader/books/:id/file?source=pdf` which returns the preserved PDF blob
 * (R2 prefix `source-pdfs/{user_id}/{id}.pdf`).
 *
 * Used by DualViewReader's "original mode" — separate from `getBookFile()` which
 * always returns the canonical readable file (the converted EPUB for these books).
 *
 * Not cached in IndexedDB by design: the source PDF is a derivative artifact, and
 * caching it under the same bookId would conflict with the EPUB blob already stored.
 * Re-fetching on each open is acceptable — original mode is opt-in.
 *
 * Throws on fetch failure rather than returning null, so the UI can show an error
 * state instead of silently rendering an empty viewer.
 */
export async function getSourcePdfFile(id) {
    if (!this.token) throw new Error('Source PDF requires authentication');
    const resp = await fetch(`${API_BASE}/reader/books/${id}/file?source=pdf`, {
        headers: { Authorization: `Bearer ${this.token}` },
    });
    if (!resp.ok) {
        throw new Error(`Source PDF fetch failed: ${resp.status}`);
    }
    return await resp.arrayBuffer();
}

// Get book file as ArrayBuffer. Tries local first, then cloud.
//
// §P3.9 Phase B: when the book is being converted incrementally (PDF
// conversion_status='partial'), the same R2 key holds a fresh EPUB on every
// chapter checkpoint. Caller passes {forceCloud: true} to skip the local
// IndexedDB cache so we always render the latest partial. We deliberately
// don't write the freshly-downloaded EPUB back to local cache during this
// phase — that would just stale-up faster than we'd serve from it.
export async function getBookFile(id, opts = {}) {
    const forceCloud = !!opts.forceCloud;
    if (!forceCloud) {
        const cached = await localDB.getBookFile(id);
        if (cached) return cached;
    }
    if (this.token) {
        return (await this.fetchBookFileFromCloud(id)) ?? null;
    }
    return null;
}

/**
 * Pre-match canonical book on upload (simplified — chunk processing moved to preheat).
 * Called as fire-and-forget after book upload so that canonical_book_id is ready
 * when the user opens the book (enables path A in initCanonicalBook).
 *
 * Invoked via the format registry: registry.epub.preProcessor → mod.process,
 * called as `mod.process.call(this, ...)` from uploadBook so that
 * `this.matchCanonicalBook` and `this.updateBook` resolve to the ApiClient.
 *
 * @param {string} bookId
 * @param {ArrayBuffer} arrayBuffer - The raw EPUB file
 * @param {Object} bookData - Book metadata (title, author, language, file_type)
 */
export async function preProcessEpubChunks(bookId, arrayBuffer, bookData) {
    if (!this.token || bookData.file_type !== 'epub') return;
    if (!isFeatureEnabled('chunks_enabled')) return;

    try {
        const JSZip = (await import('jszip')).default;
        const zip = await JSZip.loadAsync(arrayBuffer);

        const containerText = await zip.file('META-INF/container.xml')?.async('string');
        if (!containerText) return;
        const containerDoc = parseXML(containerText);
        const opfPath = containerDoc.querySelector('rootfile')?.getAttribute('full-path');
        if (!opfPath) return;

        const opfText = await zip.file(opfPath)?.async('string');
        const { spine } = await parseOPF(opfText);
        let tocItems = await parseTOC(zip, opfPath);
        if (tocItems.length === 0) {
            tocItems = spine.map((item, i) => ({ label: `Chapter ${i + 1}`, href: item.href, level: 0 }));
        }

        const emptyHTML = spine.map(() => '');
        const { bodyStarts } = mergeChapters(spine, tocItems, emptyHTML);

        // Match/create canonical book (only step — chunk processing deferred to preheat)
        const matchResult = await this.matchCanonicalBook({
            title: bookData.title,
            author: bookData.author || '',
            language: bookData.language || null,
            totalChapters: bodyStarts.length || spine.length,
        });
        if (matchResult?.canonical_book_id) {
            await this.updateBook(bookId, { canonical_book_id: matchResult.canonical_book_id });
        }
    } catch (e) {
        console.warn('[PreProcess] Failed:', e);
    }
}

// Get book file as Blob URL
export async function getBookFileUrl(id) {
    if (blobUrlCache.has(id)) {
        return blobUrlCache.get(id);
    }

    const arrayBuffer = await this.getBookFile(id);
    if (!arrayBuffer) {
        throw new Error('Book file not found');
    }

    const book = await localDB.getBook(id);
    const blob = new Blob([arrayBuffer], { type: getMimeType(book?.file_type) });
    const url = URL.createObjectURL(blob);
    blobUrlCache.set(id, url);
    return url;
}

// Save note to general_notes (Cloudflare D1) for knowledge box integration
export async function saveNoteToCloud(noteData, existingCloudNoteId = null) {
    if (!this.token) throw new Error('Not authenticated');
    const payload = existingCloudNoteId
        ? { content: noteData.content }
        : {
            content: noteData.content,
            selected_text: noteData.selected_text || null,
            source_url: `reader://book/${noteData.book_id}`,
            source_title: noteData.book_title || null,
            note_type: 'reader',
            note_category: 'knowledge',
        };
    const url = existingCloudNoteId
        ? `${API_BASE}/notes/${existingCloudNoteId}`
        : `${API_BASE}/notes`;
    const method = existingCloudNoteId ? 'PATCH' : 'POST';
    const res = await fetch(url, {
        method,
        headers: {
            'Authorization': `Bearer ${this.token}`,
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data?.message || 'Failed to save note to cloud');
    return data;
}

// Ensure a knowledge box exists for this book, add note to it
export async function ensureBookBox(bookTitle, noteId) {
    if (!this.token || !bookTitle || !noteId) return null;
    try {
        const listRes = await fetch(`${API_BASE}/note-boxes`, {
            headers: { 'Authorization': `Bearer ${this.token}` },
        });
        const listData = await listRes.json();
        const boxes = listData?.boxes || [];
        let box = boxes.find((b) => b.name === bookTitle);
        if (!box) {
            const createRes = await fetch(`${API_BASE}/note-boxes`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${this.token}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ name: bookTitle, noteIds: [noteId] }),
            });
            const createData = await createRes.json();
            if (!createRes.ok) throw new Error(createData?.message || 'Failed to create box');
            return createData?.box;
        }
        const existing = box.noteIds || [];
        if (existing.includes(noteId)) return box;
        const noteIds = [...existing, noteId];
        const patchRes = await fetch(`${API_BASE}/note-boxes/${box.id}`, {
            method: 'PATCH',
            headers: {
                'Authorization': `Bearer ${this.token}`,
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ noteIds }),
        });
        const patchData = await patchRes.json();
        if (!patchRes.ok) throw new Error(patchData?.message || 'Failed to update box');
        return patchData?.box;
    } catch (err) {
        console.warn('ensureBookBox failed:', err);
        reportError(err.message || 'ensureBookBox failed', { level: 'warn', stack: err.stack, metadata: { source: 'api.ensureBookBox', bookTitle } });
        return null;
    }
}

// ─── §F11: chapter selection at PDF upload time ─────────────────────────

/**
 * Fetch the outline-derived chapter list for a freshly-uploaded PDF.
 * Backend parses outline only (no full extractBook); response includes
 * the user's plan caps so ChapterSelectionModal can run live quota math.
 *
 * Throws on non-2xx so callers can branch on err.status.
 */
export async function previewPdfChapters(bookId) {
    if (!this.token) throw new Error('Not authenticated');
    const resp = await fetch(`${API_BASE}/reader/books/${bookId}/preview-chapters`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${this.token}` },
    });
    if (!resp.ok) {
        const data = await resp.json().catch(() => ({}));
        const e = new Error(data.error || `previewPdfChapters ${resp.status}`);
        e.status = resp.status;
        e.detail = data;
        throw e;
    }
    return resp.json();
}

/**
 * Tell the backend which outline-chapter indices the user picked.
 * Backend reserves quota for the selected page sum, snapshots it for
 * worker refunds, and enqueues the conversion message.
 *
 * Returns the new server state ({ conversion_status, selected_pages,
 * selected_chapters, total_chapters }) on success. On 4xx (quota
 * exceeded etc.) throws an Error with status + detail so the modal can
 * show a precise message without a second round-trip.
 */
export async function startPdfConversion(bookId, selectedIndices, opts = {}) {
    if (!this.token) throw new Error('Not authenticated');
    const resp = await fetch(`${API_BASE}/reader/books/${bookId}/start-conversion`, {
        method: 'POST',
        headers: {
            'Authorization': `Bearer ${this.token}`,
            'Content-Type': 'application/json',
        },
        // batch (2026-07-22): the slow half-quota lane — Mistral Batch OCR,
        // usually hours, at worst next-day. Backend ignores the flag when
        // every billable page is already cached (instant + half price).
        body: JSON.stringify({ selected_chapter_indices: selectedIndices, batch: !!opts.batch }),
    });
    if (!resp.ok) {
        const data = await resp.json().catch(() => ({}));
        const e = new Error(data.error || `startPdfConversion ${resp.status}`);
        e.status = resp.status;
        e.detail = data;
        throw e;
    }
    const result = await resp.json();
    // Mirror the backend's status flip into local IndexedDB so Library /
    // Reader observe the new state immediately (sync poll would catch up
    // eventually but the modal-close-then-card-stuck-at-pending_preview
    // window is a UX wart we avoid).
    try {
        await localDB.updateBook(bookId, {
            conversion_status: result.conversion_status || 'pending',
            selected_chapter_indices: JSON.stringify(selectedIndices),
        });
    } catch { /* best-effort */ }
    return result;
}

/**
 * Enable/disable background auto-backup of local-only books to the cloud.
 * Set by AuthContext from (isPaid && the user's autoBackup setting).
 */
export function setAutoBackupEnabled(enabled) {
    this._autoBackupEnabled = !!enabled;
}

/**
 * Background scan: upload ONE local-only book (has a local file, no file_url,
 * EPUB/MOBI) to the cloud per call. Covers both new imports and backfill of
 * existing local-only books uniformly — "no file_url + has local file" IS the
 * work list. Throttled to one book per call (the 30s sync loop drives the next).
 * Quota rejections (403) block that book for the session and surface an upgrade
 * prompt; transient errors are left for the next tick (fetchWithRetry already
 * retried within the attempt). PDFs are excluded (they already auto-upload).
 */
export async function runCloudBackupScan() {
    if (!this.token || !this._autoBackupEnabled || !this.userId) return;
    if (typeof document !== 'undefined' && document.hidden) return;
    if (!this._cloudBackupBlocked) this._cloudBackupBlocked = new Set();

    let localFileIds, books;
    try {
        [localFileIds, books] = await Promise.all([
            localDB.getLocalFileBookIds(),
            localDB.getBooks(this.userId),
        ]);
    } catch {
        return;
    }
    const next = books.find((b) =>
        b && !b.deleted_at && !b.file_url &&
        (b.file_type === 'epub' || b.file_type === 'mobi') &&
        localFileIds.has(b.id) &&
        !this._cloudBackupBlocked.has(b.id)
    );
    if (!next) return;

    try {
        await this.uploadBookToCloud(next.id);  // sets file_url on success
    } catch (e) {
        if (e?.code === 'storage_quota_exceeded' || e?.status === 403) {
            this._cloudBackupBlocked.add(next.id);
            try {
                window.dispatchEvent(new CustomEvent('ev:cloud-quota-exceeded', {
                    detail: { used_gb: e?.detail?.used_gb, limit_gb: e?.detail?.limit_gb },
                }));
            } catch { /* no window */ }
        }
        // transient / other errors: leave file_url null → retried next tick.
    }
}
